/**
 * PasswordField - a <input type="password"> with a Show/Hide toggle.
 *
 * Manage Users hands out a shared studio starter password (see
 * netlify/functions/tempPassword.mjs) that people copy from a text message.
 * A masked field makes it impossible to see whether a paste picked up a
 * stray space or a mistyped character before the mismatch shows up as a
 * failed sign-in. This toggle lets someone check what they actually typed
 * before they submit.
 *
 * The toggle button is type="button" on purpose - inside a <form>, a plain
 * <button> defaults to type="submit" and would submit the form instead of
 * just flipping visibility.
 */

import { useId, useState, type CSSProperties, type InputHTMLAttributes } from 'react';

type PasswordFieldProps = Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> & {
  wrapperStyle?: CSSProperties;
  toggleStyle?: CSSProperties;
  /** Optional context when several password controls share one screen. */
  toggleContext?: string;
};

export default function PasswordField({
  style,
  wrapperStyle,
  toggleStyle,
  toggleContext,
  id,
  ...inputProps
}: PasswordFieldProps) {
  const [visible, setVisible] = useState(false);
  const autoId = useId();
  const fieldId = id ?? autoId;

  return (
    <div style={{ position: 'relative', width: '100%', ...wrapperStyle }}>
      <input
        {...inputProps}
        id={fieldId}
        type={visible ? 'text' : 'password'}
        style={{ paddingRight: 60, ...style }}
      />
      <button
        type="button"
        onClick={() => setVisible((v) => !v)}
        aria-pressed={visible}
        aria-label={`${visible ? 'Hide' : 'Show'} ${toggleContext ?? 'password'}`}
        title={`${visible ? 'Hide' : 'Show'} ${toggleContext ?? 'password'}`}
        style={{
          position: 'absolute',
          right: 2,
          top: '50%',
          transform: 'translateY(-50%)',
          minWidth: 44,
          minHeight: 44,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          border: 'none',
          background: 'transparent',
          color: 'var(--ink-soft)',
          fontWeight: 800,
          fontSize: 12,
          letterSpacing: '0.02em',
          cursor: 'pointer',
          ...toggleStyle,
        }}
      >
        {visible ? 'Hide' : 'Show'}
      </button>
    </div>
  );
}
